/**
 * ConsumerProvider
 *
 * Holds the consumer branding for the current verification flow and provides it
 * via React context. Mounted once around all routes in App.tsx.
 *
 * The consumer comes from `?consumer=` on the iframe URL alone, exactly as before
 * VEX-7983: the Legal ID and SSN card launchers put it there, and the other
 * launchers never do, so those screens stay IDMe-only. Unknown or missing
 * consumers resolve to null, which means default styling.
 *
 * VEX-7983: `fragnoLogoEnabled` (the host's `init({ fragnoLogoEnabled })`, which
 * every SDK launcher forwards in its IDME_INIT payload) only swaps the IDMe logo
 * for the Fragno lockup; it never changes which logo is shown. With it on, the
 * shared header also uses Consent's IDME_INIT `partnerLogoUrl`, the same partner
 * logo Consent shows in its own header with the flag off.
 *
 * Testing: on non-production hosts (the same gate as /testing and the mock
 * routes) `?vex_7983_fragno_logo_enabled=` may also set the flag from the URL,
 * and wins over IDME_INIT. Production ignores it.
 *
 * IDME_INIT is also where Fragno loads (JS + CSS, see loadFragno), and only
 * with the flag on: flag off, no Fragno file is downloaded.
 */

import { createContext, useContext, useEffect, useRef, useState, ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { getValidConsumer, type SupportedConsumer } from "../shared/constants/consumers";
import { areMockRoutesEnabled } from "../shared/utils/mockRoutes";
import { isAllowedOrigin } from "../shared/utils/parentOrigin";
import { loadFragno } from "../shared/fragno/loadFragno";

interface ConsumerContextValue {
  consumer: SupportedConsumer | null;
  /**
   * VEX-7983: Fragno logo lockup and the unified header. IVA resolves this from
   * the `vex_7983_fragno_logo_enabled` Flipper flag. Off unless `true`.
   */
  fragnoLogoEnabled: boolean;
  /** A partner logo passed at runtime instead of a bundled consumer's (Consent's `partnerLogoUrl`). */
  partnerLogo: { src: string; alt: string } | null;
}

const DEFAULT_BRANDING: ConsumerContextValue = {
  consumer: null,
  fragnoLogoEnabled: false,
  partnerLogo: null,
};

const ConsumerContext = createContext<ConsumerContextValue>(DEFAULT_BRANDING);

/** Branding query params: `consumer` on every host, the Fragno flag on non-production hosts only. */
export const BRANDING_QUERY_PARAMS = {
  consumer: "consumer",
  fragnoLogoEnabled: "vex_7983_fragno_logo_enabled",
} as const;

/** `?flag`, `?flag=true` and `?flag=1` turn it on; anything else is off; absent is undefined. */
function parseFlagParam(value: string | null): boolean | undefined {
  if (value === null) return undefined;
  return value === "" || value === "true" || value === "1";
}

/** What IDME_INIT carries for branding. The consumer is never taken from it (see above). */
function brandingFromInit(payload: unknown): Omit<ConsumerContextValue, "consumer"> {
  const p = (payload ?? {}) as Record<string, unknown>;
  return {
    fragnoLogoEnabled: p.fragnoLogoEnabled === true,
    partnerLogo:
      typeof p.partnerLogoUrl === "string" && p.partnerLogoUrl
        ? { src: p.partnerLogoUrl, alt: typeof p.partnerName === "string" ? p.partnerName : "" }
        : null,
  };
}

interface ConsumerProviderProps {
  children: ReactNode;
}

export function ConsumerProvider({ children }: ConsumerProviderProps) {
  const [searchParams] = useSearchParams();
  const [fromInit, setFromInit] = useState<Omit<ConsumerContextValue, "consumer">>(DEFAULT_BRANDING);
  const fragnoParam = areMockRoutesEnabled()
    ? parseFlagParam(searchParams.get(BRANDING_QUERY_PARAMS.fragnoLogoEnabled))
    : undefined;
  // Read by the IDME_INIT handler, which is registered once.
  const fragnoParamRef = useRef(fragnoParam);
  fragnoParamRef.current = fragnoParam;

  // Same trust rule as the flows' own IDME_INIT handlers: only the embedding
  // window, and only from an IDMe-allowlisted origin.
  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (event.data?.type !== "IDME_INIT") return;
      if (event.source !== window.parent || !isAllowedOrigin(event.origin)) return;
      const branding = brandingFromInit(event.data.payload);
      setFromInit(branding);
      if (fragnoParamRef.current ?? branding.fragnoLogoEnabled) void loadFragno();
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  const fragnoLogoEnabled = fragnoParam ?? fromInit.fragnoLogoEnabled;
  const value: ConsumerContextValue = {
    consumer: getValidConsumer(searchParams.get(BRANDING_QUERY_PARAMS.consumer)),
    fragnoLogoEnabled,
    // Flag off, screens render their own headers from their own data, as before VEX-7983.
    partnerLogo: fragnoLogoEnabled ? fromInit.partnerLogo : null,
  };

  return <ConsumerContext.Provider value={value}>{children}</ConsumerContext.Provider>;
}

export function useConsumer(): ConsumerContextValue {
  return useContext(ConsumerContext);
}
